import {useChildShopId, useConfig, useShopId} from '@aboutyou/config';
import {GrpcClientProvider, GrpcConfig} from '@aboutyou/data-fetcher';
import {AB_TEST_HEADERS, useIsFeatureActive} from '@aboutyou/feature-flags';
import React, {useMemo, useState} from 'react';
import {useIsCoinsActive} from '../features/Coins/utils/useIsCoinsActive';
import {useIsMissionsActive} from '../features/Coins/utils/useIsMissionsActive';
import {useIsCouponsEnabled} from '../features/Coupons/useIsCouponsEnabled';

const nonCriticalErrorMessagePatterns: RegExp[] = [
  /timeout exceeded fetching CRM placements/,
  /no target group with any products found/,
];

export const GrpcClientHostConfigProvider: React.FC<
  React.PropsWithChildren<{config: GrpcConfig}>
> = ({config, children}) => {
  const {tadaridaUrl: hostname, gripsUrl: legacyHostname, device} = useConfig();
  const shopId = useShopId();
  const childShopId = useChildShopId();

  const grpcSettings = useMemo(
    () => ({
      debug: false,
      hostname,
      legacyHostname,
    }),
    [hostname, legacyHostname],
  );

  const legacyConfig = useMemo(
    () => ({
      device,
      shopId,
      childShopId,
    }),
    [device, shopId, childShopId],
  );

  const [abTestsConfig, setAbTestsConfig] = useState(config.abTests);

  const isCouponActive = useIsCouponsEnabled();
  const isSSOMethodsForceEnabled =
    useIsFeatureActive('force_sso_methods').isActive;
  const isMovCouponActive = useIsFeatureActive('mov_coupons').isActive;
  const isHtcCouponActive = useIsFeatureActive('high_tier_coupons').isActive;
  const isChatbotActive = useIsFeatureActive('enable_cs_chatbot').isActive;
  const isPriceOverhaulActive = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  ).isActive;
  const isCoinsActive = useIsCoinsActive();
  const isMissionsActive = useIsMissionsActive();

  const {variant, isActive: isMissionsV2RolloutActive} = useIsFeatureActive(
    'ff_missions_v2_rollout',
  );
  const isHeaderHighlightActive = useIsFeatureActive(
    'new_banner_framework',
  ).isActive;

  // Those are a temporary overrides to get the abTest config to all BE requests
  // we are doing this because there is no real ab test for this and the BE is using this `ab_coupons_rollout` and `ab_high_tier_coupons` abTest as a feature flag
  // this can be removed once the rollout is finalized
  const abTestsOverrides = [];

  if (isPriceOverhaulActive) {
    abTestsOverrides.push({
      key: 'ab_price_display_overhaul_v2',
      group: 'pdv-1',
    });
  }

  abTestsOverrides.push({key: 'ab_product_tile_v2', group: 'its_alive'});
  if (isCoinsActive) {
    abTestsOverrides.push({key: 'ab_coins_rollout', group: 'coi-1'});
  }

  if (isCoinsActive && isMissionsActive) {
    abTestsOverrides.push({key: 'ab_missions_rollout', group: 'mr-1'});
  }

  if (isCouponActive) {
    abTestsOverrides.push({key: 'ab_coupons_rollout', group: 'cour-1'});
  }

  if (isSSOMethodsForceEnabled) {
    abTestsOverrides.push({key: 'ab_force_sso_buttons', group: 'sso-1'});
  }

  if (isMovCouponActive) {
    abTestsOverrides.push({key: 'ab_enable_mov_coupons', group: 'emc-1'});
  }

  if (isHtcCouponActive) {
    abTestsOverrides.push({key: 'ab_high_tier_coupons', group: 'htc-1'});
  }

  if (isMissionsV2RolloutActive) {
    abTestsOverrides.push({
      key: 'ab_missions_claiming',
      group: variant,
    });
  }

  if (isHeaderHighlightActive) {
    abTestsOverrides.push({
      key: 'ab_header_highlight',
      group: 'hih-1',
    });
  }

  // NOTE: Adding this to th request invalidates all recorded tapes for unit tests
  // just skipping it for tests now, but need a followup to figure out how to handle this properly
  if (process.env.NODE_ENV !== 'test') {
    abTestsOverrides.push({key: 'ab_subdomain_dropped_shop', group: 'drop-1'});
  }

  if (isChatbotActive) {
    abTestsOverrides.push({key: 'ab_chatbot', group: 'cb-1'});
  }

  const temporaryMergedConfig = {
    ...config,
    abTests: [...abTestsConfig, ...abTestsOverrides].filter(Boolean),
  } as GrpcConfig;

  return (
    <GrpcClientProvider
      config={temporaryMergedConfig}
      legacyConfig={legacyConfig}
      settings={grpcSettings}
      nonCriticalErrorMessagePatterns={nonCriticalErrorMessagePatterns}
      abTestOptions={{
        abTestHeaderName: AB_TEST_HEADERS.TadaridaConsideredABTests,
      }}
      updateAbTestConfig={setAbTestsConfig}
    >
      {children}
    </GrpcClientProvider>
  );
};

export const GrpcClientHostConfigProviderAppCheckout: React.FC<
  React.PropsWithChildren<{config: GrpcConfig}>
> = ({config, children}) => {
  const {tadaridaUrl: hostname, gripsUrl: legacyHostname, device} = useConfig();
  const shopId = useShopId();
  const childShopId = useChildShopId();

  const grpcSettings = useMemo(
    () => ({
      debug: false,
      hostname,
      legacyHostname,
    }),
    [hostname, legacyHostname],
  );

  const legacyConfig = useMemo(
    () => ({
      device,
      shopId,
      childShopId,
    }),
    [device, shopId, childShopId],
  );

  return (
    <GrpcClientProvider
      config={config}
      legacyConfig={legacyConfig}
      settings={grpcSettings}
    >
      {children}
    </GrpcClientProvider>
  );
};
